/**
 * Operations API - Fuel logs, events, expenses, telematics, KPIs
 * @module api/operations
 */

import { apiRequest, getAuthHeaders } from './core.js';

// =============================================================================
// FUEL LOG API
// =============================================================================

/**
 * Fuel Log API - Fuel consumption tracking
 */
export const fuelLogAPI = {
  getAll: () => apiRequest('/fuel_log?_limit=5000'),
  getById: (id) => apiRequest(`/fuel_log/${id}`),
  getByVehicle: (vehicleId) => apiRequest(`/fuel_log?vehicle_id=${vehicleId}`),
  getByDriver: (driverId) => apiRequest(`/fuel_log?driver_id=${driverId}`),
  getByFuelType: (fuelType) => apiRequest(`/fuel_log?fuel_type=${fuelType}`),
  create: (fuelLog) => apiRequest('/fuel_log', {
    method: 'POST',
    body: JSON.stringify(fuelLog),
  }),
  update: (id, fuelLog) => apiRequest(`/fuel_log/${id}`, {
    method: 'PUT',
    body: JSON.stringify(fuelLog),
  }),
  delete: (id) => apiRequest(`/fuel_log/${id}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// FUEL API (orchestrated — creates fuel_log + expense + event + resource reading)
// =============================================================================

/**
 * Fuel API — Orchestrated refueling & consumable top-up
 * Uses multipart/form-data for photo uploads
 */
export const fuelAPI = {
  create: (formData) => apiRequest('/fuel', {
    method: 'POST',
    body: formData, // FormData for multipart
  }),
};

// =============================================================================
// FUEL CARD API
// =============================================================================

/**
 * Fuel Card API - Card management, CSV import, reconciliation
 */
export const fuelCardAPI = {
  // Cards CRUD
  getAll: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/fuel-cards${qs ? '?' + qs : ''}`);
  },
  getById: (id) => apiRequest(`/fuel-cards/${id}`),
  create: (card) => apiRequest('/fuel-cards', { method: 'POST', body: JSON.stringify(card) }),
  update: (id, card) => apiRequest(`/fuel-cards/${id}`, { method: 'PUT', body: JSON.stringify(card) }),
  delete: (id) => apiRequest(`/fuel-cards/${id}`, { method: 'DELETE' }),

  // Transactions
  getTransactions: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/fuel-cards/transactions/list${qs ? '?' + qs : ''}`);
  },

  // CSV Import
  importCSV: async (file, provider) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('provider', provider);
    // Use raw fetch since apiRequest adds Content-Type: application/json
    const apiBase = process.env.REACT_APP_API_URL || '/api/v1';
    const resp = await fetch(`${apiBase}/fuel-cards/import`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });
    return resp.json();
  },

  // Import history
  getImportHistory: () => apiRequest('/fuel-cards/imports/history'),

  // Consumption tracking (monthly spend vs ceiling)
  getConsumption: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/fuel-cards/consumption/monthly${qs ? '?' + qs : ''}`);
  },

  // Reconciliation
  reconcile: (threshold = 80) => apiRequest('/fuel-cards/reconcile', {
    method: 'POST',
    body: JSON.stringify({ threshold }),
  }),
  manualMatch: (transactionId, fuelLogId) => apiRequest(`/fuel-cards/transactions/${transactionId}/match`, {
    method: 'POST',
    body: JSON.stringify({ fuel_log_id: fuelLogId }),
  }),

  // Stats
  getStats: () => apiRequest('/fuel-cards/stats/summary'),

  // Prix-carburants.gouv.fr open data
  refreshPrices: (limit = 500) => apiRequest(`/fuel-cards/prices/refresh?limit=${limit}`, { method: 'POST' }),
  searchNearbyStations: (lat, lon, radius = 15, fuelType = null) => {
    const params = new URLSearchParams({ lat, lon, radius });
    if (fuelType) params.append('fuel_type', fuelType);
    return apiRequest(`/fuel-cards/prices/nearby?${params}`);
  },
  validatePrice: (lat, lon, fuelType, pricePerLiter) => apiRequest('/fuel-cards/prices/validate', {
    method: 'POST',
    body: JSON.stringify({ lat, lon, fuel_type: fuelType, price_per_liter: pricePerLiter }),
  }),
  getPriceStatus: () => apiRequest('/fuel-cards/prices/status'),
};

// =============================================================================
// EVENT API
// =============================================================================

/**
 * Event API - Fleet events tracking
 */
export const eventAPI = {
  getAll: () => apiRequest('/event?_limit=5000'),
  getById: (id) => apiRequest(`/event/${id}`),
  getByVehicle: (vehicleId) => apiRequest(`/event?vehicle_id=${vehicleId}`),
  getByDriver: (driverId) => apiRequest(`/event?driver_id=${driverId}`),
  getByCategory: (category) => apiRequest(`/event?category=${category}`),
  getByStatus: (status) => apiRequest(`/event?status=${status}`),
  create: (event) => apiRequest('/event', {
    method: 'POST',
    body: JSON.stringify(event),
  }),
  update: (id, event) => apiRequest(`/event/${id}`, {
    method: 'PUT',
    body: JSON.stringify(event),
  }),
  delete: (id) => apiRequest(`/event/${id}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// EXPENSE API
// =============================================================================

/**
 * Expense API - Cost tracking
 */
export const expenseAPI = {
  getAll: () => apiRequest('/expense'),
  getById: (id) => apiRequest(`/expense/${id}`),
  getByEvent: (eventId) => apiRequest(`/expense?event_id=${eventId}`),
  getByVehicle: (vehicleId) => apiRequest(`/expense?vehicle_id=${vehicleId}`),
  getByStatus: (status) => apiRequest(`/expense?status=${status}`),
  getByCategory: (category) => apiRequest(`/expense?category=${category}`),
  create: (expense) => apiRequest('/expense', {
    method: 'POST',
    body: JSON.stringify(expense),
  }),
  update: (id, expense) => apiRequest(`/expense/${id}`, {
    method: 'PUT',
    body: JSON.stringify(expense),
  }),
  delete: (id) => apiRequest(`/expense/${id}`, {
    method: 'DELETE',
  }),
};

/**
 * Expense Document API - Receipts and invoices
 */
export const expenseDocumentAPI = {
  getAll: () => apiRequest('/expense_document'),
  getById: (id) => apiRequest(`/expense_document/${id}`),
  getByExpense: (expenseId) => apiRequest(`/expense_document?expense_id=${expenseId}`),
  create: (document) => apiRequest('/expense_document', {
    method: 'POST',
    body: JSON.stringify(document),
  }),
  update: (id, document) => apiRequest(`/expense_document/${id}`, {
    method: 'PUT',
    body: JSON.stringify(document),
  }),
  delete: (id) => apiRequest(`/expense_document/${id}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// TELEMATICS ALERT API
// =============================================================================

/**
 * Telematics Alert API - IoT device alerts
 */
export const telematicsAlertAPI = {
  getAll: () => apiRequest('/telematics_alert'),
  getById: (id) => apiRequest(`/telematics_alert/${id}`),
  getByVehicle: (vehicleId) => apiRequest(`/telematics_alert?vehicle_id=${vehicleId}`),
  getBySeverity: (severity) => apiRequest(`/telematics_alert?severity=${severity}`),
  getUnresolved: () => apiRequest('/telematics_alert?resolved=0'),
  create: (alert) => apiRequest('/telematics_alert', {
    method: 'POST',
    body: JSON.stringify(alert),
  }),
  update: (id, alert) => apiRequest(`/telematics_alert/${id}`, {
    method: 'PUT',
    body: JSON.stringify(alert),
  }),
  resolve: (id, resolvedBy, notes) => apiRequest(`/telematics_alert/${id}`, {
    method: 'PUT',
    body: JSON.stringify({
      resolved: 1,
      resolved_at: new Date().toISOString(),
      resolved_by: resolvedBy,
      notes: notes,
    }),
  }),
  delete: (id) => apiRequest(`/telematics_alert/${id}`, {
    method: 'DELETE',
  }),
};

// =============================================================================
// TELEMATICS API (GPS Pairing)
// =============================================================================

/**
 * Telematics API - GPS provider pairing and management
 */
export const telematicsAPI = {
  /**
   * Pair vehicles with a GPS provider
   * @param {Object} pairingData - { vehicle_ids: number[], provider_code: string }
   */
  pair: (pairingData) => apiRequest('/telematics/pair', {
    method: 'POST',
    body: JSON.stringify(pairingData),
  }),

  /**
   * Unpair vehicles from GPS provider
   * @param {number[]} vehicleIds - Array of vehicle IDs to unpair
   */
  unpair: (vehicleIds) => apiRequest('/telematics/unpair', {
    method: 'POST',
    body: JSON.stringify({ vehicle_ids: Array.isArray(vehicleIds) ? vehicleIds : [vehicleIds] }),
  }),

  /**
   * Get all vehicle-to-provider mappings
   */
  getMappings: () => apiRequest('/telematics/mappings'),

  /**
   * Get mappings for a specific vehicle
   * @param {number} vehicleId - Vehicle ID
   */
  getMappingsByVehicle: (vehicleId) => apiRequest(`/telematics/mappings?vehicle_id=${vehicleId}`),

  /**
   * Get mappings for a specific provider
   * @param {string} providerCode - Provider code
   */
  getMappingsByProvider: (providerCode) => apiRequest(`/telematics/mappings?provider_code=${providerCode}`),

  /**
   * Get current GPS positions for all vehicles (latest telemetry per vehicle)
   */
  getCurrentPositions: () => apiRequest('/vehicle_telemetry?_sort=-timestamp&_limit=100'),

  /**
   * Get telemetry history for a specific vehicle
   * @param {number} vehicleId - Vehicle ID
   * @param {number} limit - Number of records (default 100)
   */
  getVehicleTelemetry: (vehicleId, limit = 100) =>
    apiRequest(`/vehicle_telemetry?vehicle_id=${vehicleId}&_sort=-timestamp&_limit=${limit}`),

  /**
   * Get latest position for a specific vehicle
   * @param {number} vehicleId - Vehicle ID
   */
  getLatestPosition: (vehicleId) =>
    apiRequest(`/vehicle_telemetry?vehicle_id=${vehicleId}&_sort=-timestamp&_limit=1`),
};

// Note: For GPS positions, query vehicle_telemetry with _sort=-timestamp&_limit=1 per vehicle

// =============================================================================
// KPI API
// =============================================================================

/**
 * KPI API - Key performance indicators
 */
export const kpiAPI = {
  getAll: () => apiRequest('/kpis'),
  getCostBreakdown: (period = 'month') => apiRequest(`/kpis/costs?period=${period}`),
  getCostsByVehicle: (period = 'month') => apiRequest(`/kpis/costs/by-vehicle?period=${period}`),
  getCostsByOrg: (period = 'month') => apiRequest(`/kpis/costs/by-org?period=${period}`),
};

// =============================================================================
// HEALTH CHECK API
// =============================================================================

/**
 * Health Check API - System health
 */
export const healthAPI = {
  check: () => apiRequest('/health'),
};

// =============================================================================
// DOSSIER API
// =============================================================================

/**
 * Dossier API - Case file aggregation
 */
export const dossierAPI = {
  getVehicle: (vehicleId) => apiRequest(`/dossier/vehicle/${vehicleId}`),
  getClaim: (claimId) => apiRequest(`/dossier/claim/${claimId}`),
  getMaintenance: (maintenanceId) => apiRequest(`/dossier/maintenance/${maintenanceId}`),
  getDriver: (driverId) => apiRequest(`/dossier/driver/${driverId}`),
  search: (query, types) => {
    const params = new URLSearchParams({ q: query });
    if (types) params.append('types', types.join(','));
    return apiRequest(`/dossier/search?${params.toString()}`);
  },
};

// =============================================================================
// AI PILOT API
// =============================================================================

/**
 * AI Pilot API - Prompt generation
 */
/**
 * Driver Badge API — toll and parking badge management
 */
export const driverBadgeAPI = {
  getAll: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/driver-badges${qs ? '?' + qs : ''}`);
  },
  getById: (id) => apiRequest(`/driver-badges/${id}`),
  create: (badge) => apiRequest('/driver-badges', { method: 'POST', body: JSON.stringify(badge) }),
  update: (id, badge) => apiRequest(`/driver-badges/${id}`, { method: 'PUT', body: JSON.stringify(badge) }),
  delete: (id) => apiRequest(`/driver-badges/${id}`, { method: 'DELETE' }),
  // Consumption tracking (monthly spend vs ceiling)
  getConsumption: (params = {}) => {
    const qs = new URLSearchParams(params).toString();
    return apiRequest(`/driver-badges/consumption/monthly${qs ? '?' + qs : ''}`);
  },
};

/**
 * Controle Technique API — CT recording, history, expiry tracking
 */
/**
 * TVS API — Fleet Fiscal Intelligence (ex-TVS / CO2 / Polluants / ZFE)
 */
/**
 * Budget API — Fleet Budget Planning & Control
 */
export const budgetAPI = {
  list: () => apiRequest('/budget'),
  get: (id) => apiRequest(`/budget/${id}`),
  create: (data) => apiRequest('/budget', { method: 'POST', body: JSON.stringify(data) }),
  update: (id, data) => apiRequest(`/budget/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id) => apiRequest(`/budget/${id}`, { method: 'DELETE' }),
  getLines: (id) => apiRequest(`/budget/${id}/lines`),
  upsertLine: (id, line) => apiRequest(`/budget/${id}/lines`, { method: 'POST', body: JSON.stringify(line) }),
  getComparison: (id) => apiRequest(`/budget/${id}/comparison`),
  autoFill: (id) => apiRequest(`/budget/${id}/auto-fill`, { method: 'POST' }),
  freeze: (id) => apiRequest(`/budget/${id}/freeze`, { method: 'POST' }),
  activate: (id) => apiRequest(`/budget/${id}/activate`, { method: 'POST' }),
};

export const tvsAPI = {
  getFleetTax: (year) => apiRequest(`/tvs/fleet-tax?year=${year}`),
  getVehicleTax: (vehicleId, year) => apiRequest(`/tvs/vehicle/${vehicleId}?year=${year}`),
  getVehicleProjection: (vehicleId) => apiRequest(`/tvs/vehicle/${vehicleId}/projection`),
  simulateReplacement: (data) => apiRequest('/tvs/simulate-replacement', { method: 'POST', body: JSON.stringify(data) }),
  getBrackets: (year) => apiRequest(`/tvs/brackets?year=${year}`),
  getZFEZones: () => apiRequest('/tvs/zfe/zones'),
  getFleetZFE: (zone) => apiRequest(`/tvs/zfe/fleet?zone=${zone}`),
  getVehicleZFE: (vehicleId) => apiRequest(`/tvs/zfe/vehicle/${vehicleId}`),
  getFleetAllZones: () => apiRequest('/tvs/zfe/fleet-all'),
};

export const ctAPI = {
  record: (data) => apiRequest('/ct/record', { method: 'POST', body: JSON.stringify(data) }),
  getHistory: (vehicleId) => apiRequest(`/ct/vehicle/${vehicleId}`),
  getExpiring: (days = 60) => apiRequest(`/ct/expiring?days=${days}`),
};

export const aiPilotAPI = {
  generateFeaturePrompt: (featureId, options = {}) => apiRequest(`/generate_prompt/feature/${featureId}`, {
    method: 'POST',
    body: JSON.stringify(options),
  }),
  generateBugPrompt: (bugId, options = {}) => apiRequest(`/generate_prompt/bug/${bugId}`, {
    method: 'POST',
    body: JSON.stringify(options),
  }),
  previewFeaturePrompt: (featureId, templateType = 'feature') =>
    apiRequest(`/generate_prompt/preview/feature/${featureId}?templateType=${templateType}`),
  getStats: () => apiRequest('/generate_prompt/stats'),
  getTemplates: () => apiRequest('/ai_prompt_template'),
  getProjectStack: () => apiRequest('/project_stack'),
};
